/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import localization from '../../shared/strings';
import { getStatusFromResponceError } from '../../shared/utils';
import { FILTER, LIST_LIMIT_MAX, PAGE_SIZE } from './constants';
import isApiListFilter from './helpers/isApiListFilter';
import parseReportFilename from './helpers/parseReportFilename';

const l10n = localization.OPERATIONS;
const toastL10n = localization.TOAST.OPERATIONS;

function splitBatchPayload(payload = {}) {
  const {
    items = [],
    itemsTotal = 0,
    ...summary
  } = payload;
  return { items, itemsTotal, summary };
}

function evictIfFilterMismatch(state, summary) {
  if (!summary || !summary._id) return;
  if (!isApiListFilter(state.filter)) return;
  if (summary.status === state.filter) return;
  if (summary._id === state.expandedId || summary._id === state.deepLinkId) return;
  const index = state.items.findIndex((item) => item._id === summary._id);
  if (index < 0) return;
  state.items.splice(index, 1);
  if (state.total > 0) {
    state.total -= 1;
  }
}

function upsertSummary(state, summary) {
  if (!summary || !summary._id) return;
  const index = state.items.findIndex((item) => item._id === summary._id);
  if (index >= 0) {
    state.items[index] = { ...state.items[index], ...summary };
    evictIfFilterMismatch(state, state.items[index]);
    return;
  }
  const keep = summary._id === state.expandedId || summary._id === state.deepLinkId;
  if (isApiListFilter(state.filter) && summary.status !== state.filter && !keep) {
    return;
  }
  state.items.unshift(summary);
}

function ensureDetails(state, batchId) {
  if (!state.details[batchId]) {
    state.details[batchId] = {
      items: [],
      itemsTotal: 0,
      loading: false,
      error: null,
    };
  }
  return state.details[batchId];
}

export const fetchList = createAsyncThunk(
  'batchOperations/fetchList',
  async ({ skip = 0, silent = false, limit = PAGE_SIZE } = {}, { extra: { sdk, Logger }, getState }) => {
    const { filter } = getState().batchOperations;
    const params = {
      limit: Math.min(limit, LIST_LIMIT_MAX),
      skip,
    };
    if (isApiListFilter(filter)) {
      params.status = filter;
    }

    try {
      const { data } = await sdk.products.listBatchOperations(params);
      return {
        batches: data.batches || [],
        total: data.total || 0,
        skip,
        silent,
        filter,
      };
    } catch (error) {
      Logger.error(new Error('Error fetching batch operations'), { error }, [
        'FetchBatchOperationsFailed',
        { errorMessage: error?.message || 'NoMessage' },
      ]);
      throw error;
    }
  },
);

export const fetchMore = createAsyncThunk(
  'batchOperations/fetchMore',
  async (_, { extra: { sdk, Logger }, getState }) => {
    const { filter, listCount } = getState().batchOperations;
    const skip = listCount;
    const params = {
      limit: PAGE_SIZE,
      skip,
    };
    if (isApiListFilter(filter)) {
      params.status = filter;
    }

    try {
      const { data } = await sdk.products.listBatchOperations(params);
      return {
        batches: data.batches || [],
        total: data.total || 0,
        skip,
        filter,
      };
    } catch (error) {
      Logger.error(new Error('Error fetching more batch operations'), { error }, [
        'FetchMoreBatchOperationsFailed',
        { errorMessage: error?.message || 'NoMessage' },
      ]);
      throw error;
    }
  },
);

export const fetchOne = createAsyncThunk(
  'batchOperations/fetchOne',
  async ({
    batchId,
    skip = 0,
    limit = PAGE_SIZE,
    append = false,
    summaryOnly = false,
  }, { extra: { sdk, Logger } }) => {
    try {
      const { data } = await sdk.products.getBatchOperation(batchId, {
        limit: summaryOnly ? 1 : limit,
        skip: summaryOnly ? 0 : skip,
      });
      return {
        payload: data,
        append,
        summaryOnly,
      };
    } catch (error) {
      Logger.error(new Error('Error fetching batch operation'), { error, batchId }, [
        'FetchBatchOperationFailed',
        { errorMessage: error?.message || 'NoMessage', batchId: String(batchId) },
      ]);
      throw error;
    }
  },
);

export const downloadReport = createAsyncThunk(
  'batchOperations/downloadReport',
  async (batchId, {
    extra: {
      sdk, Logger, Toast, utils,
    },
  }) => {
    try {
      const response = await sdk.products.downloadBatchOperationReport(batchId);
      const filename = parseReportFilename(
        response.headers && response.headers['content-disposition'],
        l10n.csvFallbackFilename,
      );
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      utils.saveFile(blob, filename);
      return batchId;
    } catch (error) {
      const status = getStatusFromResponceError(error);
      if (status === 409) {
        Toast(toastL10n.reportNotReady, { type: 'warning' });
        return null;
      }
      Logger.error(new Error('Error downloading batch operation report'), { error, batchId }, [
        'DownloadBatchOperationReportFailed',
        { errorMessage: error?.message || 'NoMessage', batchId: String(batchId) },
      ]);
      Toast(toastL10n.downloadError, { type: 'error' });
      throw error;
    }
  },
  {
    condition: (batchId, { getState }) => !getState().batchOperations.reportDownloadingById[batchId],
  },
);

const initialState = {
  items: [],
  total: 0,
  skip: 0,
  listCount: 0,
  listRequestId: null,
  moreRequestId: null,
  filter: FILTER.all,
  loading: false,
  loadingMore: false,
  error: null,
  expandedId: null,
  deepLinkId: null,
  details: {},
  operationsScreenActive: false,
  reportDownloadingById: {},
};

const batchOperationsSlice = createSlice({
  name: 'batchOperations',
  initialState,
  reducers: {
    setFilter(state, action) {
      state.filter = action.payload || FILTER.all;
      state.skip = 0;
      state.listCount = 0;
      state.loadingMore = false;
      state.moreRequestId = null;
    },
    setExpandedId(state, action) {
      state.expandedId = action.payload || null;
    },
    setDeepLinkId(state, action) {
      state.deepLinkId = action.payload || null;
    },
    setOperationsScreenActive(state, action) {
      state.operationsScreenActive = Boolean(action.payload);
    },
    upsertBatchFromEvent(state, action) {
      const {
        batchId,
        operationType,
        status,
        total,
        succeeded = 0,
        warnings = 0,
        failed = 0,
        skipped = 0,
        createdBy,
        salesChannels,
        createdAt,
      } = action.payload || {};
      if (!batchId) return;

      const processed = succeeded + warnings + failed + skipped;
      const index = state.items.findIndex((item) => item._id === batchId);
      const terminalStatuses = [FILTER.completed, FILTER.failed];
      const inFlightStatuses = [FILTER.queued, FILTER.running];

      if (index >= 0) {
        const existing = state.items[index];
        if (
          terminalStatuses.includes(existing.status)
          && inFlightStatuses.includes(status)
        ) {
          return;
        }

        state.items[index] = {
          ...existing,
          ...(operationType ? { operationType } : {}),
          ...(status ? { status } : {}),
          total: total != null ? total : existing.total,
          succeeded,
          warnings,
          failed,
          skipped,
          processed: Math.max(existing.processed || 0, processed),
          ...(createdBy ? { createdBy } : {}),
          ...(salesChannels ? { salesChannels } : {}),
          ...(createdAt ? { createdAt } : {}),
        };
        evictIfFilterMismatch(state, state.items[index]);
        return;
      }

      const summary = {
        _id: batchId,
        operationType,
        status,
        total: total != null ? total : 0,
        succeeded,
        warnings,
        failed,
        skipped,
        processed,
        createdBy: createdBy || null,
        salesChannels: salesChannels || [],
        createdAt,
      };
      const keep = batchId === state.expandedId || batchId === state.deepLinkId;
      if (isApiListFilter(state.filter) && summary.status !== state.filter && !keep) {
        return;
      }
      state.items.unshift(summary);
      state.total += 1;
      if (state.listCount < state.items.length) {
        state.listCount = state.items.length;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchList.pending, (state, action) => {
        if (!action.meta.arg?.silent) {
          state.listRequestId = action.meta.requestId;
          state.loading = true;
          state.error = null;
          state.loadingMore = false;
          state.moreRequestId = null;
        }
      })
      .addCase(fetchList.fulfilled, (state, action) => {
        const {
          batches, total, skip, silent, filter,
        } = action.payload;
        if (filter !== state.filter) {
          if (!silent && action.meta.requestId === state.listRequestId) {
            state.loading = false;
          }
          return;
        }
        if (!silent && action.meta.requestId !== state.listRequestId) return;
        const keepIds = [state.expandedId, state.deepLinkId].filter(Boolean);
        const kept = state.items.filter((item) => (
          keepIds.includes(item._id) && !batches.some((batch) => batch._id === item._id)
        ));
        state.items = [...batches, ...kept];
        state.total = total;
        state.skip = skip;
        state.listCount = batches.length;
        if (!silent) {
          state.loading = false;
          state.error = null;
        }
      })
      .addCase(fetchList.rejected, (state, action) => {
        if (action.meta?.condition) return;
        if (!action.meta.arg?.silent) {
          if (action.meta.requestId !== state.listRequestId) return;
          state.loading = false;
          state.error = action.error;
        }
      })
      .addCase(fetchMore.pending, (state, action) => {
        state.moreRequestId = action.meta.requestId;
        state.loadingMore = true;
      })
      .addCase(fetchMore.fulfilled, (state, action) => {
        const {
          batches, total, skip, filter,
        } = action.payload;
        if (filter !== state.filter) return;
        if (action.meta.requestId !== state.moreRequestId) return;
        const existingIds = new Set(state.items.map((item) => item._id));
        const next = batches.filter((batch) => !existingIds.has(batch._id));
        state.items = [...state.items, ...next];
        state.total = total;
        state.skip = skip;
        state.listCount = skip + batches.length;
        state.loadingMore = false;
      })
      .addCase(fetchMore.rejected, (state, action) => {
        if (action.meta.requestId !== state.moreRequestId) return;
        state.loadingMore = false;
      })
      .addCase(fetchOne.pending, (state, action) => {
        const { batchId, append, summaryOnly } = action.meta.arg || {};
        if (!batchId || summaryOnly) return;
        const details = ensureDetails(state, batchId);
        details.loading = true;
        details.error = null;
        if (!append) {
          details.items = details.items || [];
        }
      })
      .addCase(fetchOne.fulfilled, (state, action) => {
        const { payload, append, summaryOnly } = action.payload;
        const { items, itemsTotal, summary } = splitBatchPayload(payload);
        upsertSummary(state, summary);
        if (summaryOnly || !summary._id) return;
        const details = ensureDetails(state, summary._id);
        details.loading = false;
        details.error = null;
        details.itemsTotal = itemsTotal;
        details.items = append ? [...details.items, ...items] : items;
      })
      .addCase(fetchOne.rejected, (state, action) => {
        const { batchId, summaryOnly } = action.meta.arg || {};
        if (!batchId || summaryOnly) return;
        const details = ensureDetails(state, batchId);
        details.loading = false;
        details.error = action.error;
      })
      .addCase(downloadReport.pending, (state, action) => {
        state.reportDownloadingById[action.meta.arg] = true;
      })
      .addCase(downloadReport.fulfilled, (state, action) => {
        const batchId = action.meta.arg;
        delete state.reportDownloadingById[batchId];
      })
      .addCase(downloadReport.rejected, (state, action) => {
        if (action.meta?.condition) return;
        const batchId = action.meta.arg;
        delete state.reportDownloadingById[batchId];
      });
  },
});

export const {
  setFilter,
  setExpandedId,
  setDeepLinkId,
  setOperationsScreenActive,
  upsertBatchFromEvent,
} = batchOperationsSlice.actions;

export {
  BATCH_OPERATION_LIVE_SOCKET_EVENTS,
  BATCH_OPERATION_SOCKET_EVENTS,
} from './constants';

export default batchOperationsSlice.reducer;
